import { Box } from '@mui/material';
import type { CategoryId } from '@timelog/pm-shared';
import {
  categoryCarriesCost,
  categoryCost,
  defaultFactorOf,
  getCategory,
  margin,
  upliftIsVisible,
} from '@timelog/pm-shared';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { CategoryKebab } from './CategoryKebab';
import { CATEGORY_COLORS } from './categoryStyles';
import {
  parseAmountInput,
  parseFactorInput,
  parseMarginInput,
  useContractEditing,
  useFinancials,
} from './editor';
import { useGuidedEntry } from './flow';

import { MaterialSymbol } from '@/components/MaterialSymbol';
import { InlineTextCell } from '@/components/planning/cells';
import type { CommitIntent } from '@/components/planning/cells';
import { CONTRACT_TYPE_STYLES } from '@/components/planning/ContractChip';
import {
  formatAmount,
  formatAmountAbbrev,
  factorDigits,
  formatFactorEntered,
  formatPct,
  groupDigitsWhileTyping,
} from '@/lib';

/**
 * The bento card shell shared by every category: accent dot + label, the
 * factor chip (pink "override" state when off the config's per-section default,
 * with reset), the kebab, the editable amount, the budgeted-cost line (CALC-9) and
 * a slot for card-specific content (hours, splits, suppliers).
 *
 * A category billed via a sibling (BR-10) renders frozen: badge instead of
 * factor chip, no editing — "Use contract default" in the kebab takes it back.
 */

export type CategoryCardProps = {
  categoryId: CategoryId;
  span?: 1 | 2;
  /** Rendered between the amount and the budgeted-cost line. */
  children?: ReactNode;
  /** Extra content at the card's end (split sections). */
  footer?: ReactNode;
};

export const CategoryCard = ({ categoryId, span = 1, children, footer }: CategoryCardProps) => {
  const { state, dispatch, effectiveContracts, consumption, budgetModel, calcOptions } =
    useFinancials();
  const { isEditing, startEditing, stopEditing, advanceFrom } = useContractEditing();
  const guided = useGuidedEntry();
  const { t } = useTranslation(['pmFinancials', 'pmCommon']);

  const category = getCategory(state.document, categoryId);
  const label = t(`pmCommon:category.${categoryId}`);
  const color = CATEGORY_COLORS[categoryId];
  const moved = category.billedVia !== undefined;
  const movedTarget = moved
    ? effectiveContracts.find((contract) => contract.id === category.billedVia)
    : undefined;

  const flowStep = guided.activeStepFor(categoryId);
  const amountFlowActive = flowStep !== null && flowStep !== 'ownHours';
  // Uplift hidden for this section (CALC-20, ADR 0046): there is no pill at
  // all — the section carries no uplift and operates at 1.0 through
  // `calcOptions` (the cost line below reads it), overrides and reset never
  // appear — the config accessor decides, never the variant key.
  const metric = budgetModel.config.metric;
  const upliftOn = upliftIsVisible(budgetModel.config, categoryId);
  const defaultFactor = defaultFactorOf(budgetModel.config, categoryId);
  const factorOverridden = upliftOn && Math.abs(category.targetFactor - defaultFactor) > 0.001;
  const cost = categoryCost(category, calcOptions);

  const amountKey = `amount:${categoryId}`;
  const factorKey = `factor:${categoryId}`;

  const commitAmount = (raw: string, intent: CommitIntent = 'commit') => {
    const amount = parseAmountInput(raw);
    if (amountFlowActive) {
      guided.commit(amount);
      return;
    }
    if (amount !== null) {
      dispatch({ type: 'SET_CATEGORY_AMOUNT', id: categoryId, amount });
    }
    advanceFrom(amountKey, intent);
  };

  return (
    <Box
      data-automation-id="category-card"
      data-category={categoryId}
      data-moved={moved ? 'true' : 'false'}
      // CategoryGrid's container queries read this; an inline `gridColumn` here
      // would win against them and re-break the one-column layout.
      data-category-span={String(span)}
      sx={{
        // Lets the card shrink inside its grid track instead of forcing the
        // track wider than the panel, which is what clipped the cards.
        minWidth: 0,
        bgcolor: 'var(--bg-panel)',
        border: `1px solid ${amountFlowActive || flowStep === 'ownHours' ? 'var(--color-pink-80)' : 'var(--border-divider)'}`,
        borderRadius: 'var(--border-radius-x-large)',
        boxShadow: amountFlowActive || flowStep === 'ownHours' ? 'var(--focus-ring-brand)' : 'none',
        transition: 'border-color var(--duration-base) ease, box-shadow var(--duration-base) ease',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ px: 2, pt: 2, pb: footer ? '10px' : '14px', flex: 1 }}>
        {/* header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            /*
             * 4px, not 8. At three columns a card's content box is ~161px and the
             * label plus the factor chip and kebab need ~153px — the wider gap
             * tipped "Contingency" into truncation. `space-between` already
             * separates them; this is only the floor when they meet.
             */
            columnGap: 0.5,
            rowGap: 0.5,
            mb: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
            <Box
              component="span"
              sx={{
                width: 8,
                height: 8,
                borderRadius: '2px',
                bgcolor: color,
                flex: 'none',
                opacity: moved ? 0.35 : 1,
              }}
            />
            {/*
              Truncate rather than clip. The factor chip and kebab beside this are
              `flex: 'none'`, so in a narrow track the browser shrinks the label
              instead of wrapping them — without an ellipsis that reads as a
              rendering bug ("Contingenc") rather than an intentional shortening.
            */}
            <Box
              component="span"
              title={label}
              sx={{
                fontSize: 14,
                fontWeight: 500,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {label}
            </Box>
            {moved && movedTarget ? (
              <Box
                component="span"
                data-automation-id="category-moved-badge"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1,
                  py: 0.5,
                  borderRadius: 'var(--border-radius-pill)',
                  bgcolor: CONTRACT_TYPE_STYLES[movedTarget.type].bg,
                  fontSize: 10,
                  fontWeight: 500,
                  color: CONTRACT_TYPE_STYLES[movedTarget.type].fg,
                  whiteSpace: 'nowrap',
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                <MaterialSymbol name="move_up" size={12} />
                {t('categoryCard.BilledVia', { name: movedTarget.name })}
              </Box>
            ) : null}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 'none' }}>
            {/* Names the chip, which on its own was a bare "×3.0". Factor metric only:
                under the margin metric the chip is a percentage, not a factor. */}
            {!moved && upliftOn && metric === 'factor' ? (
              <Box
                component="span"
                data-automation-id={`category-factor-label-${categoryId}`}
                sx={{ fontSize: 11, color: 'var(--fg-3)', whiteSpace: 'nowrap' }}
              >
                {t('categoryCard.BudgetFactor')}
              </Box>
            ) : null}
            {!moved && upliftOn ? (
              <InlineTextCell
                automationId={`category-factor-${categoryId}`}
                value={
                  metric === 'factor'
                    ? factorDigits(category.targetFactor)
                    : String(Math.round(margin(category.targetFactor) * 1000) / 10)
                }
                editing={isEditing(factorKey)}
                onStartEdit={() => startEditing(factorKey)}
                onCancel={stopEditing}
                advanceOnEnter
                onCommit={(raw, intent) => {
                  // Margin metric: the field takes a percentage, converted to a
                  // factor on commit (ADR 0027) — storage is always factors.
                  const factor =
                    metric === 'factor' ? parseFactorInput(raw) : parseMarginInput(raw);
                  if (factor !== null) {
                    dispatch({ type: 'SET_CATEGORY_FACTOR', id: categoryId, factor });
                  }
                  advanceFrom(factorKey, intent);
                }}
                inputWidth={44}
                align="right"
                inputSx={{ fontFamily: 'var(--font-family-mono)', fontSize: 11 }}
                displaySx={{ p: 0, m: 0 }}
                display={
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      px: 1,
                      py: 0.5,
                      borderRadius: 'var(--border-radius-pill)',
                      // Neutral whatever the value (2026-10-12, the pilot's ask): a
                      // changed factor is not a warning, so no "override" word or colour.
                      bgcolor: 'var(--bg-sunken)',
                      border: '1px solid var(--border-divider)',
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontFamily: 'var(--font-family-mono)',
                        fontSize: 11,
                        color: 'var(--fg-3)',
                      }}
                    >
                      {metric === 'factor'
                        ? formatFactorEntered(category.targetFactor)
                        : formatPct(margin(category.targetFactor) * 100)}
                    </Box>
                    {factorOverridden ? (
                      <>
                        <Box
                          component="span"
                          data-automation-id={`category-factor-reset-${categoryId}`}
                          title={t('categoryCard.ResetToDefault')}
                          onClick={(event) => {
                            event.stopPropagation();
                            // The default rides the flag payload since schema 2,
                            // so the reducer is told it rather than looking it up.
                            dispatch({
                              type: 'RESET_CATEGORY_FACTOR',
                              id: categoryId,
                              factor: defaultFactor,
                            });
                          }}
                          sx={{ display: 'inline-flex', color: 'var(--fg-3)', cursor: 'pointer' }}
                        >
                          <MaterialSymbol name="refresh" size={12} />
                        </Box>
                      </>
                    ) : null}
                  </Box>
                }
              />
            ) : null}
            <CategoryKebab categoryId={categoryId} />
          </Box>
        </Box>

        {/* amount */}
        <Box sx={{ mb: 0.5, opacity: moved ? 0.55 : 1 }}>
          {moved ? (
            <Box sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.5 }}>
              <Box
                component="span"
                data-automation-id={`category-amount-${categoryId}`}
                sx={{ fontFamily: 'var(--font-family-mono)', fontSize: 20, fontWeight: 500 }}
              >
                {formatAmountAbbrev(category.amount)}
              </Box>
              <Box component="span" sx={{ fontSize: 11, color: 'var(--fg-3)' }}>
                DKK
              </Box>
            </Box>
          ) : (
            <InlineTextCell
              automationId={`category-amount-${categoryId}`}
              formatWhileTyping={groupDigitsWhileTyping}
              value={category.amount > 0 ? formatAmount(category.amount) : ''}
              editing={amountFlowActive || isEditing(amountKey)}
              onStartEdit={() => startEditing(amountKey)}
              onCancel={amountFlowActive ? guided.exit : stopEditing}
              advanceOnEnter={!amountFlowActive}
              onCommit={(raw, intent) => {
                if (amountFlowActive && intent === 'tab-next') {
                  // Tab in the walk = leave it at 0 and move on.
                  if (raw.trim() === '') {
                    guided.skip();
                    return;
                  }
                }
                commitAmount(raw, intent);
              }}
              placeholder="0"
              inputWidth={140}
              inputSx={{ fontFamily: 'var(--font-family-mono)', fontSize: 20, fontWeight: 500 }}
              display={
                <Box sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.5 }}>
                  <Box
                    component="span"
                    sx={{
                      fontFamily: 'var(--font-family-mono)',
                      fontSize: 20,
                      fontWeight: 500,
                      color: category.amount > 0 ? 'var(--fg-1)' : 'var(--fg-3)',
                    }}
                  >
                    {formatAmountAbbrev(category.amount)}
                  </Box>
                  <Box component="span" sx={{ fontSize: 11, color: 'var(--fg-3)' }}>
                    DKK
                  </Box>
                </Box>
              }
            />
          )}
        </Box>
        {amountFlowActive ? (
          <Box sx={{ fontSize: 11, color: 'var(--fg-3)', mb: 1 }}>
            {t('categoryCard.HintSubtract')}
          </Box>
        ) : null}

        {children}

        {/* budgeted cost (CALC-9) — not on a cost-less card: Contingency is a
            buffer, nothing is spent to earn it (BR-14), so there is no number here */}
        {categoryCarriesCost(categoryId) ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              columnGap: 1,
              mt: 1,
              mb: 1,
              opacity: moved ? 0.55 : 1,
            }}
          >
            <Box component="span" sx={{ fontSize: 11, color: 'var(--fg-3)' }}>
              {t('categoryCard.BudgetedCost')}
            </Box>
            <Box
              component="span"
              data-automation-id={`category-cost-${categoryId}`}
              sx={{ fontFamily: 'var(--font-family-mono)', fontSize: 14, fontWeight: 500 }}
            >
              {moved ? '—' : `${formatAmountAbbrev(cost)} DKK`}
            </Box>
          </Box>
        ) : null}

        {/* allocation: the plan consumes Own Work (CALC-15); nothing draws
            down the other categories in v1 (OQ-5) — no fake bars, and no
            caption saying so either (removed 2026-10-14, the designer's ask). */}
        {!moved && categoryId === 'own' ? (
          <OwnConsumptionBar
            consumption={consumption}
            contractId={state.contractId}
            basis={budgetModel.config.budgetBasis}
          />
        ) : null}
      </Box>

      {footer}
    </Box>
  );
};

/**
 * CALC-15/21: the plan's consumption of the Own Work budget. The budget basis
 * (ADR 0027) picks the pair: cost basis reads planned cost against budgeted
 * cost; revenue basis reads planned revenue against the card's amount, with a
 * " revenue" caption suffix. Nothing planned → a caption, never a 0% fill
 * (absent ≠ zero).
 */
const OwnConsumptionBar = ({
  consumption,
  contractId,
  basis,
}: {
  consumption: {
    contractId: string;
    ownRevenueBudget: number;
    ownCostBudget: number;
    consumedAmount: number;
    consumedCost: number;
  }[];
  contractId: string;
  basis: 'cost' | 'revenue';
}) => {
  const { t } = useTranslation('pmFinancials');
  const row = consumption.find((candidate) => candidate.contractId === contractId);
  const budget =
    row === undefined ? 0 : basis === 'cost' ? row.ownCostBudget : row.ownRevenueBudget;
  const consumed = row === undefined ? 0 : basis === 'cost' ? row.consumedCost : row.consumedAmount;
  if (!row || budget <= 0) {
    return <Box sx={{ fontSize: 11, color: 'var(--fg-3)' }}>{t('categoryCard.NoBudgetYet')}</Box>;
  }
  if (consumed <= 0) {
    return (
      <Box data-automation-id="own-consumption" sx={{ fontSize: 11, color: 'var(--fg-3)' }}>
        {t('categoryCard.NothingPlanned')}
      </Box>
    );
  }
  const pct = Math.min(100, Math.round((consumed / budget) * 100));
  return (
    <Box data-automation-id="own-consumption">
      <Box
        sx={{
          height: 4,
          bgcolor: 'var(--bg-sunken)',
          borderRadius: '100px',
          overflow: 'hidden',
          mb: 0.5,
        }}
      >
        <Box
          sx={{
            height: '100%',
            width: `${pct}%`,
            bgcolor: 'var(--color-pink-80)',
            transition: 'width var(--duration-slow) var(--easing-standard)',
          }}
        />
      </Box>
      <Box sx={{ fontSize: 11, color: 'var(--fg-3)' }}>
        {t(basis === 'revenue' ? 'categoryCard.PlannedRevenue' : 'categoryCard.Planned', {
          pct,
          consumed: formatAmountAbbrev(consumed),
          budget: formatAmountAbbrev(budget),
        })}
      </Box>
    </Box>
  );
};
